/**
 * Score System
 * Manages current score, level, and persistent high score
 */

export interface ScoreState {
  current: number;
  highScore: number;
  level: number;
}

export class ScoreSystem {
  private state: ScoreState;
  private readonly STORAGE_KEY = 'simon-revolutions-high-score';

  constructor() {
    this.state = {
      current: 0,
      highScore: this.loadHighScore(),
      level: 1
    };
  }

  /**
   * Award points for completing a sequence.
   * Official formula: sequenceLength * 10
   * @param sequenceLength Length of completed sequence
   * @param panicMultiplier Optional score multiplier from panic mode (default: 1.0)
   */
  addPointsForSequence(sequenceLength: number, panicMultiplier = 1.0): void {
    const basePoints = sequenceLength * 10;
    const points = Math.floor(basePoints * panicMultiplier);
    this.state.current += points;

    if (this.state.current > this.state.highScore) {
      this.state.highScore = this.state.current;
      this.saveHighScore();
    }
  }

  /**
   * Increment level after a successful round
   */
  levelUp(): void {
    this.state.level += 1;
  }

  /**
   * Deduct points as penalty (for intruders or mistakes)
   * Prevents score from going negative
   */
  deductPoints(amount: number): void {
    this.state.current = Math.max(0, this.state.current - amount);
  }

  /**
   * Get current score state
   */
  getState(): ScoreState {
    return { ...this.state };
  }

  /**
   * Reset current score and level for a new game, preserving high score
   */
  reset(): void {
    this.state.current = 0;
    this.state.level = 1;
  }

  /**
   * Load high score from localStorage
   */
  private loadHighScore(): number {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? parseInt(stored, 10) : 0;
  }

  /**
   * Save high score to localStorage
   */
  private saveHighScore(): void {
    localStorage.setItem(this.STORAGE_KEY, this.state.highScore.toString());
  }
}
